// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * The filing workbench (design doc §8.3, §8.4, §10.2, §10.3, US-A-01/02/05/06/09), as a
 * dialog opened from the draft list (pages/ArchiveCreate.tsx): empty for a new machine, or
 * on a draft's device number to resume it. Filing navigates to the new record; closing
 * earlier loses nothing, because the form is a draft by then.
 *
 * Which half of the form appears follows from the product type's `has_bom`, exactly as the
 * server decides it — a type with a BOM gets the scannable slot list, one without gets
 * free-form entries.
 *
 * Three behaviours here are specified rather than invented:
 *
 * - **the remaining count is always on screen and submit stays disabled until it is zero**
 *   (US-A-02, US-A-06). The server re-checks regardless; this exists so the operator
 *   learns about the gap while still holding the scanner;
 * - **drafts are kept in this browser and on the server** (§10.2, `drafts/store.ts`). Saved
 *   as the operator works, restored on return — on this workstation or another — deleted on
 *   success, and kept on a 400, a 409 or a dropped connection, which is when the work still
 *   matters;
 * - **the device ID and the SN may both be left blank**: the number can be filled in on the
 *   archive page later, and an SN is typed only when the label already has one — otherwise
 *   it is generated at filing;
 * - **one idempotency key per attempt** (§7.7): generated when the form is filled and reused
 *   across retries, so a flaky network cannot file the same machine twice and burn a second
 *   archive number.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { newIdempotencyKey } from "@/api/client";
import { api } from "@/api/endpoints";
import type { BomDetail, FiledArchive, InstalledSerial, ProductType } from "@/api/types";
import { AddLabel } from "@/components/AddLabel";
import { moduleTypeLabel } from "@/components/BomModulesSummary";
import { ErrorBanner } from "@/components/ErrorBanner";
import { FoldControls } from "@/components/FoldControls";
import { ChevronIcon, InfoIcon } from "@/components/icons";
import { Modal } from "@/components/Modal";
import { ModuleSlotsPanel } from "@/components/ModuleSlotsPanel";
import { Pager } from "@/components/Pager";
import { ResizableTable } from "@/components/ResizableTable";
import { ScrappedSerialPrompt } from "@/components/ScrappedSerialPrompt";
import { BarcodeMismatchNote, SerialStatusNote } from "@/components/SerialStatusNote";
import { SnCard } from "@/components/SnCard";
import { deleteDraft, loadDraft, saveDraft, type WorkflowType } from "@/drafts/store";
import { useAvailableBoms } from "@/hooks/useAvailableBoms";
import { type BarcodeRules, useBarcodeRules } from "@/scanner/barcodeRules";
import { isRobotSn, moduleSnType } from "@/scanner/moduleCodes";
import { scanEnterHandlers } from "@/scanner/scanEnter";
import { normalizeDeviceNo, normalizeSerial } from "@/scanner/serials";
import {
  expandModuleSlots,
  type ModuleScanOutcome,
  type ModuleSlot,
  useModuleSlots,
} from "@/scanner/useModuleSlots";
import { type SerialStatus, useSerialStatus } from "@/scanner/useSerialStatus";
import {
  type BomRow,
  expandRows,
  expandSlots,
  type ScanOutcome,
  useSlotScanner,
} from "@/scanner/useSlotScanner";
import { type FoldNode, foldedToLevel, hiddenKeys, levelCount, parentKeys } from "@/tree/fold";

interface FreeEntry {
  position_name: string;
  quantity: number;
  serial: string;
  /** Held as strings because they are form fields; blanks become null on submit. */
  material_code: string;
}

const BLANK_ENTRY: FreeEntry = {
  position_name: "",
  quantity: 1,
  serial: "",
  material_code: "",
};

/** How often the open form is saved as a draft. */
export const DRAFT_SAVE_INTERVAL_MS = 10_000;

export interface DraftPayload {
  deviceNo: string;
  productTypeId: string;
  bomId: string;
  values: Record<string, string>;
  entries: FreeEntry[];
  /** Module codes by module slot; absent on a draft saved before modules existed. */
  modules?: Record<string, string>;
  /** A module's filing, and its type. Its SN is generated when it is filed. */
  kind?: ArchiveKind;
  moduleType?: string;
  /** An SN already on the label, typed in; absent or blank, one is generated at filing. */
  sn?: string;
}

export type ArchiveKind = "ROBOT" | "MODULE";

/** A robot's drafts are keyed by device number, a module's by its module SN (§12). */
export function draftWorkflow(kind: ArchiveKind): WorkflowType {
  return kind === "MODULE" ? "module-create" : "archive-create";
}

/** Filing a module from inside another filing (§9.3): what the slot fixes, and where the
 *  new module's SN goes once it is filed. */
export interface NestedFiling {
  productTypeId: string;
  bomId: string;
  moduleType: string;
  /** The slot it is filed for, for the title. */
  positionName: string;
  onFiled: (filed: FiledArchive) => void;
}

/**
 * Where a draft is kept before the robot has a number: a key of its own per form, so what is
 * scanned before the number is typed is saved too. Once the number is in, the draft moves
 * under it and this one is removed — as when a number is corrected.
 */
export const UNNAMED_DRAFT_PREFIX = "~unnamed~";

/** What a draft save did: wrote (or found nothing new to write), had nothing to keep, or
 *  could not write — storage unavailable. */
type SaveOutcome = "saved" | "nothing" | "failed";

export function isUnnamedDraft(subject: string): boolean {
  return subject.startsWith(UNNAMED_DRAFT_PREFIX);
}

/** How much of a draft is filled in: serials scanned, or free entries added. */
export function enteredCount(payload: DraftPayload): number {
  return (
    Object.values(payload.values).filter(Boolean).length +
    Object.values(payload.modules ?? {}).filter(Boolean).length +
    payload.entries.length
  );
}

export function ArchiveCreateDialog({
  onClose,
  initialDeviceNo = "",
  initialDraftSubject,
  kind = "ROBOT",
  nested,
}: {
  onClose: () => void;
  /** A draft's device number, to open on that draft. */
  initialDeviceNo?: string;
  /** A draft saved before it had a device number (or a module draft, by its SN), to open
   *  on that one. */
  initialDraftSubject?: string;
  /** A robot, or a module filed on its own at a sub-assembly station (§9.1). */
  kind?: ArchiveKind;
  /** Set when filing a module on the spot from another filing (§9.3). */
  nested?: NestedFiling;
}) {
  const { t, i18n } = useTranslation();
  const isModule = kind === "MODULE";
  const workflow = draftWorkflow(kind);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const productTypes = useQuery({
    queryKey: ["product-types", "ACTIVE"],
    queryFn: () => api.productTypes.list({ status: "ACTIVE", limit: 200 }),
  });

  const [productTypeId, setProductTypeId] = useState(nested?.productTypeId ?? "");
  const [bomId, setBomId] = useState(nested?.bomId ?? "");
  const [moduleType, setModuleType] = useState(nested?.moduleType ?? "");
  /** The module last filed on the spot from this filing, with the SN it was given — its
   *  barcode label is printed from here, before the scanning carries on. */
  const [filedOnSpot, setFiledOnSpot] = useState<FiledArchive | null>(null);
  /** The module slot being filed on the spot, in a nested dialog. */
  const [fillingSlot, setFillingSlot] = useState<ModuleSlot | null>(null);
  const [deviceNo, setDeviceNo] = useState(initialDeviceNo);
  /** The SN already on the label, when there is one; blank, the server generates it. */
  const [sn, setSn] = useState("");
  const [serialWidth, setSerialWidth] = useState<number | null>(null);
  const [entries, setEntries] = useState<FreeEntry[]>([]);
  const [restoredAt, setRestoredAt] = useState<number | null>(null);
  /** When the form was last saved as a draft, shown so the operator knows it was. */
  const [savedAt, setSavedAt] = useState<number | null>(null);
  /** The subject whose stored draft has been read back, so saving may resume. */
  const [hydratedSubject, setHydratedSubject] = useState<string | null>(null);
  // Held for the whole attempt so a retry after a timeout is the *same* request (§7.7).
  const idempotencyKey = useRef(newIdempotencyKey());

  const productType: ProductType | undefined = productTypes.data?.items.find(
    (item) => item.id === productTypeId,
  );

  const boms = useAvailableBoms(productTypeId, Boolean(productType?.has_bom));

  const bom = useQuery({
    queryKey: ["bom", bomId],
    queryFn: () => api.boms.get(bomId),
    enabled: Boolean(bomId),
  });

  const bomModules = useQuery({
    queryKey: ["bom", bomId, "modules"],
    queryFn: () => api.boms.modules(bomId),
    enabled: Boolean(bomId),
  });
  const moduleTypes = useQuery({ queryKey: ["module-types"], queryFn: api.moduleTypes.list });
  const typeLabel = (code: string) =>
    moduleTypeLabel(code, moduleTypes.data ?? [], i18n.language === "en-US");

  // What this filing holds (§2.2): the robot's own lines, or one module's — never a lower
  // module's, which is a module slot instead. Counted within one of the scope.
  const scope = isModule ? moduleType || null : null;
  const scopeItems = useMemo(() => {
    const items = (bom.data as BomDetail | undefined)?.items ?? [];
    if (isModule && !scope) return [];
    return items
      .filter((item) => (item.owner_module_type ?? null) === scope)
      .map((item) => ({
        ...item,
        effective_quantity: item.scope_quantity ?? item.effective_quantity,
      }));
  }, [bom.data, isModule, scope]);
  const rows = useMemo(() => expandRows(scopeItems), [scopeItems]);
  // The scanner still sees only the slots that take a serial. The counted lines are on
  // screen so the operator can see what is being filed, not so it can be typed into.
  const slots = useMemo(() => expandSlots(scopeItems), [scopeItems]);
  // Every slot's serial must pass its material's barcode rules, checked as it lands.
  const barcodeRules = useBarcodeRules(
    useMemo(() => scopeItems.map((item) => item.material_code), [scopeItems]),
  );
  const scanner = useSlotScanner(slots, barcodeRules.accepts);

  const moduleSlots = useMemo(() => {
    const data = bomModules.data;
    if (!data || (isModule && !scope)) return [];
    const holder = isModule
      ? data.modules.find((module) => module.module_type === scope)
      : data.robot;
    return expandModuleSlots(
      holder?.module_slots ?? [],
      (bom.data as BomDetail | undefined)?.items ?? [],
    );
  }, [bomModules.data, bom.data, isModule, scope]);
  const modules = useModuleSlots(moduleSlots, bomId);
  const usesBom = isModule || (productType?.has_bom ?? false);
  /** Every code the filing needs is in: serials and modules alike. */
  const isComplete = scanner.isComplete && modules.isComplete;
  const scanTotal = slots.length + moduleSlots.length;
  const scanDone = scanner.filledCount + modules.filledCount;

  // Every serial on screen, whichever half of the form holds it, checked against what is
  // installed elsewhere as it is entered (§7.5).
  const enteredSerials = useMemo(
    () => (usesBom ? Object.values(scanner.values) : entries.map((entry) => entry.serial)),
    [usesBom, scanner.values, entries],
  );
  const serialStatus = useSerialStatus(enteredSerials);
  const installed = serialStatus.installed;

  // --- drafts (§10.2) -----------------------------------------------------------------
  // Keyed by user and device number, so a shift change never hands the next operator
  // someone else's half-scanned machine.
  const [unnamedSubject] = useState(
    () => initialDraftSubject ?? `${UNNAMED_DRAFT_PREFIX}${newIdempotencyKey()}`,
  );
  // A module has nothing to key its draft by until it is filed, so it keeps its own key.
  const draftSubject = (isModule ? "" : normalizeDeviceNo(deviceNo)) || unnamedSubject;
  const userId = me.data?.user_id;

  // The draft list behind this dialog reads the same key.
  const refreshDrafts = () =>
    queryClient.invalidateQueries({ queryKey: ["archive-drafts", userId] });

  function clearForm() {
    scanner.clear();
    modules.clear();
    setEntries([]);
    setRestoredAt(null);
    setSavedAt(null);
    // The draft goes with it, so whatever is entered next is new and saves afresh.
    lastSaved.current = "";
    ownSubject.current = null;
  }

  useEffect(() => {
    if (!userId || !draftSubject) return;
    let cancelled = false;
    setHydratedSubject(null);
    setSavedAt(null);
    void loadDraft<DraftPayload>(userId, workflow, draftSubject).then((draft) => {
      if (cancelled) return;
      if (draft) {
        // This is the draft being worked on now: saved under another key later (a number
        // typed, or corrected), this copy is the one removed.
        ownSubject.current = draftSubject;
        setProductTypeId(draft.payload.productTypeId);
        setBomId(draft.payload.bomId);
        setEntries(draft.payload.entries);
        scanner.restore(draft.payload.values);
        modules.restore(draft.payload.modules ?? {});
        if (draft.payload.moduleType) setModuleType(draft.payload.moduleType);
        setSn(draft.payload.sn ?? "");
        setRestoredAt(draft.updatedAt);
      }
      // Marked hydrated either way — the saving effect below is waiting on this, and a
      // device number with no draft must not stay unsaveable.
      setHydratedSubject(draftSubject);
    });
    return () => {
      cancelled = true;
    };
    // Only on a change of who or which machine: re-running on every keystroke would fight
    // the operator's typing.
  }, [userId, draftSubject]);

  const hasContent =
    Object.values(scanner.values).some(Boolean) ||
    Object.values(modules.values).some(Boolean) ||
    entries.length > 0;

  // Saved on a timer, not per keystroke: every DRAFT_SAVE_INTERVAL_MS while something has
  // changed, and once more when the dialog closes or the tab is hidden, so what was
  // scanned since the last tick is not lost with it. The timer reads the form through a
  // ref, so it is set up once rather than restarted by every change.
  const latest = useRef({ userId, draftSubject, hydratedSubject, hasContent, payload: {} });
  latest.current = {
    userId,
    draftSubject,
    hydratedSubject,
    hasContent,
    payload: {
      deviceNo,
      productTypeId,
      bomId,
      values: scanner.values,
      entries,
      modules: modules.values,
      sn,
      ...(isModule ? { kind, moduleType } : {}),
    },
  };
  /** What was last written, to skip a tick when nothing changed. */
  const lastSaved = useRef("");
  /** The device number this dialog's draft is stored under. One machine, one draft: when
   * the number is corrected, the copy under the old one is removed. */
  const ownSubject = useRef<string | null>(null);
  /** Set once filed: the draft is deleted then, and a closing save must not bring it back. */
  const filed = useRef(false);
  const mounted = useRef(true);
  /** The save under way, if any. Saves run one after another, and whatever deletes the
   * draft waits for the one under way first: its PUT could otherwise land after the
   * DELETE and bring back the draft of a machine just filed or discarded. */
  const saving = useRef<Promise<SaveOutcome>>(Promise.resolve("nothing"));

  const writeDraft = useRef(async (): Promise<SaveOutcome> => {
    const {
      userId: user,
      draftSubject: subject,
      hydratedSubject: hydrated,
      hasContent: content,
      payload,
    } = latest.current;
    // Two guards, both load-bearing.
    //
    // `hydrated`: the load above is asynchronous, so without it a save could run first
    // with an empty form and overwrite the very draft that is being read back.
    //
    // `content`: an untouched form is not a draft, and saving it would list a machine with
    // nothing entered.
    if (filed.current || !user || !subject || hydrated !== subject || !content) return "nothing";
    const serialized = JSON.stringify([subject, payload]);
    if (serialized === lastSaved.current) return "saved";
    if (!(await saveDraft<DraftPayload>(user, workflow, subject, payload as DraftPayload)))
      return "failed";
    lastSaved.current = serialized;
    const previous = ownSubject.current;
    ownSubject.current = subject;
    if (previous && previous !== subject) await deleteDraft(user, workflow, previous);
    if (mounted.current) setSavedAt(Date.now());
    void queryClient.invalidateQueries({ queryKey: ["archive-drafts", user] });
    return "saved";
  });
  const saveNow = useRef((): Promise<SaveOutcome> => {
    saving.current = saving.current.then(() => writeDraft.current());
    return saving.current;
  });

  /** Removes the draft for good: under the key it is shown by now, and under the one it was
   * last stored by, which differs when the number was typed after the last save. */
  async function dropDraft(user: string) {
    const stored = ownSubject.current;
    await saving.current;
    const subjects = new Set(
      [draftSubject, stored, ownSubject.current].filter((subject): subject is string =>
        Boolean(subject),
      ),
    );
    await Promise.all([...subjects].map((subject) => deleteDraft(user, workflow, subject)));
  }

  useEffect(() => {
    mounted.current = true;
    const save = () => void saveNow.current();
    const timer = window.setInterval(save, DRAFT_SAVE_INTERVAL_MS);
    const onHidden = () => {
      if (document.visibilityState === "hidden") save();
    };
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", save);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("pagehide", save);
      mounted.current = false;
      save();
    };
  }, []);

  // --- closing --------------------------------------------------------------------------

  // Closing asks first — ×, Escape and the backdrop all come here — because a stray click
  // ends a form someone may have spent twenty scans on. The answer says what happens to
  // the work: the draft is saved before asking, so it can say so (or say that it failed).
  const [closing, setClosing] = useState<SaveOutcome | null>(null);
  /** 「丢弃草稿」 asks first: what it throws away is everything scanned so far. */
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const pristine = !productTypeId && !deviceNo.trim() && !sn.trim() && !hasContent;

  async function requestClose() {
    if (pristine || filed.current) {
      onClose();
      return;
    }
    let outcome = await saveNow.current();
    // Something is entered but nothing was saved: the draft under a number typed a moment
    // ago is still being read, and saving waits for that. It takes milliseconds.
    for (
      let attempt = 0;
      outcome === "nothing" && latest.current.hasContent && attempt < 10;
      attempt++
    ) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      outcome = await saveNow.current();
    }
    setClosing(outcome);
  }

  // --- submission ---------------------------------------------------------------------

  const file = useMutation({
    mutationFn: () =>
      api.archives.file(
        isModule
          ? {
              kind: "MODULE",
              product_type_id: productTypeId,
              bom_id: bomId,
              module_type: moduleType,
              serials_by_slot: scanner.values,
              modules_by_slot: modules.modulesBySlot,
              sn: normalizeSerial(sn) || null,
            }
          : {
              product_type_id: productTypeId,
              // Blank is "not yet": the number may be filled in on the archive page later.
              device_no: normalizeDeviceNo(deviceNo) || null,
              sn: normalizeSerial(sn) || null,
              bom_id: productType?.has_bom ? bomId : null,
              serials_by_slot: productType?.has_bom ? scanner.values : {},
              modules_by_slot: productType?.has_bom ? modules.modulesBySlot : {},
              // Trimmed, and a blank becomes null rather than an empty string: "" would be
              // stored as a material number that is present and says nothing.
              entries: productType?.has_bom
                ? []
                : entries.map((entry) => ({
                    // Field by field: a draft saved before 物料名称 was dropped still has one.
                    position_name: entry.position_name,
                    quantity: entry.quantity,
                    serial: entry.serial,
                    material_code: entry.material_code.trim() || null,
                  })),
            },
        idempotencyKey.current,
      ),
    onSuccess: async (result) => {
      filed.current = true;
      // Only now: a 400 or a dropped connection must leave the scanning intact.
      if (userId) await dropDraft(userId);
      await refreshDrafts();
      if (nested) {
        // Back to the filing it was opened from, with this module in its slot (§9.3).
        nested.onFiled(result);
        onClose();
      } else {
        navigate(`/archives/${result.archive_id}`);
      }
    },
  });

  // An SN typed must have the shape the scanning screens recognize it by — a robot SN, or
  // a module SN of this module's type — or it would be read as something else later. The
  // server refuses one that is already on an archive.
  const typedSn = normalizeSerial(sn);
  const snInvalid =
    typedSn !== "" && (isModule ? moduleSnType(typedSn) !== moduleType : !isRobotSn(typedSn));

  const canSubmit =
    Boolean(productTypeId) &&
    (!isModule || Boolean(moduleType)) &&
    !snInvalid &&
    (usesBom ? Boolean(bomId) && isComplete : entries.length > 0) &&
    // The server would refuse it with SERIAL_ALREADY_INSTALLED; the warnings above already
    // say which part and where it is.
    installed.size === 0 &&
    !file.isPending;

  return (
    <Modal
      title={
        nested
          ? t("moduleFiling.nestedTitle", { position: nested.positionName })
          : isModule
            ? t("moduleFiling.title")
            : t("archive.createTitle")
      }
      onClose={requestClose}
      wide
      // In the footer, which stays put while the slots scroll: on a 48-slot BOM the submit
      // button and the count were a long scroll below the last row anyone was looking at.
      footer={
        <div className="submit-row filing-footer">
          <button
            type="button"
            className="button-primary"
            disabled={!canSubmit}
            // Why it is greyed, on hover: the full sentence would make the footer two lines
            // tall, and the count beside it already says how many are missing.
            title={usesBom && !isComplete ? t("archive.incompleteHint") : undefined}
            onClick={() => file.mutate()}
          >
            {t("common.submit")}
          </button>
          {usesBom && scanTotal > 0 && (
            <span className={isComplete ? "filing-count is-complete" : "filing-count"}>
              {t("archive.progress", {
                done: scanDone,
                total: scanTotal,
                remaining: scanTotal - scanDone,
              })}
            </span>
          )}
          {installed.size > 0 && <p className="hint">{t("archive.installedBlocksSubmit")}</p>}
          {savedAt && (
            <p className="hint draft-saved">
              {t("archive.draftSaved", { time: new Date(savedAt).toLocaleTimeString() })}
            </p>
          )}
        </div>
      }
    >
      <ErrorBanner error={file.error ?? boms.error} />

      {restoredAt && (
        <div className="banner banner-info">
          {t("archive.draftRestored", { time: new Date(restoredAt).toLocaleString() })}
          <button type="button" onClick={() => setConfirmingDiscard(true)}>
            {t("archive.draftDiscard")}
          </button>
        </div>
      )}

      <div className="panel">
        <div className="form-row">
          <label className="required" htmlFor="product-type">
            {t("archive.productType")}
          </label>
          <select
            id="product-type"
            required
            value={productTypeId}
            disabled={Boolean(nested)}
            onChange={(event) => {
              setProductTypeId(event.target.value);
              setBomId("");
              setModuleType("");
              scanner.clear();
              modules.clear();
            }}
          >
            <option value="">—</option>
            {/* A module is always filed against a BOM: the BOM is what defines it (§2). */}
            {(productTypes.data?.items ?? [])
              .filter((item) => !isModule || item.has_bom)
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
          </select>
        </div>

        {!isModule && (
          <div className="form-row">
            <label htmlFor="device-no">{t("archive.device")}</label>
            <input
              id="device-no"
              placeholder={t("archive.deviceOptional")}
              value={deviceNo}
              onChange={(event) => setDeviceNo(event.target.value)}
              {...scanEnterHandlers<HTMLInputElement>(() => {
                // The number is in: on to the scanning, if the slots are already there.
                document.getElementById("scan")?.focus();
              })}
              autoComplete="off"
            />
          </div>
        )}
        {!isModule && (
          <SnField
            label={t("archive.robotSnLabel")}
            value={sn}
            onChange={setSn}
            invalid={snInvalid ? t("archive.snInvalid") : null}
            width={serialWidth}
          />
        )}
      </div>

      {!productType ? (
        // Which half of the form applies follows from the product type, so until one is
        // chosen neither is shown — offering free entries here would invite an operator to
        // fill in a form the server is going to reject.
        <p className="hint">{t("archive.chooseProductType")}</p>
      ) : usesBom ? (
        <BomSlots
          rows={rows}
          bomOptions={boms.items}
          bomPagination={boms}
          bomId={bomId}
          bomLocked={Boolean(nested)}
          onSerialWidth={setSerialWidth}
          onBomChange={(value) => {
            setBomId(value);
            if (!nested) setModuleType("");
            scanner.clear();
            modules.clear();
          }}
          scanner={scanner}
          barcodeRules={barcodeRules}
          installed={installed}
          serialStatus={serialStatus}
          progress={{ done: scanDone, total: scanTotal, complete: isComplete }}
          nextModule={moduleSlots.find((slot) => slot.positionCode === modules.nextSlot)}
          onScan={(raw) => {
            // Modules first (§9.2): a module code goes to a module slot of its type, a
            // robot SN is refused, and anything else is a part's serial.
            const fitted = modules.scan(raw);
            if (fitted) return fitted;
            if (isRobotSn(raw)) return { kind: "robot-sn" };
            return scanner.scan(raw);
          }}
          header={
            isModule &&
            bomId && (
              <>
                <div className="form-row">
                  <label className="required" htmlFor="module-type">
                    {t("moduleFiling.moduleType")}
                  </label>
                  <select
                    id="module-type"
                    required
                    value={moduleType}
                    disabled={Boolean(nested)}
                    onChange={(event) => {
                      setModuleType(event.target.value);
                      scanner.clear();
                      modules.clear();
                    }}
                  >
                    <option value="">—</option>
                    {(bomModules.data?.modules ?? []).map((module) => (
                      <option key={module.module_type} value={module.module_type ?? ""}>
                        {typeLabel(module.module_type ?? "")}
                        {module.position_code ? ` · ${module.position_code}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                {bomModules.data && bomModules.data.modules.length === 0 && (
                  <p className="hint">{t("moduleFiling.noModules")}</p>
                )}
                {moduleType && (
                  <SnField
                    label={t("moduleFiling.snLabel")}
                    value={sn}
                    onChange={setSn}
                    width={serialWidth}
                    invalid={
                      snInvalid
                        ? t("moduleFiling.snInvalid", { type: typeLabel(moduleType) })
                        : null
                    }
                  />
                )}
              </>
            )
          }
          moduleSection={
            <>
              {/* Its SN exists now that it is filed: the label goes on before it is fitted. */}
              {filedOnSpot?.module_sn && (
                <SnCard
                  sn={filedOnSpot.module_sn}
                  symbology="code128"
                  label={t("moduleFiling.filedOnSpot", { archiveNo: filedOnSpot.archive_no })}
                  printLines={[
                    filedOnSpot.archive_no,
                    typeLabel(filedOnSpot.module_type ?? ""),
                  ].filter(Boolean)}
                />
              )}
              <ModuleSlotsPanel
                slots={moduleSlots}
                modules={modules}
                types={moduleTypes.data ?? []}
                onFileHere={(slot) => setFillingSlot(slot)}
              />
            </>
          }
        />
      ) : (
        <FreeEntries
          entries={entries}
          onChange={setEntries}
          installed={installed}
          serialStatus={serialStatus}
        />
      )}

      {fillingSlot && (
        // Filed on its own, straight away (§9.3): if this filing is then abandoned, the
        // module is not lost — it is simply in stock.
        <ArchiveCreateDialog
          kind="MODULE"
          nested={{
            productTypeId,
            bomId,
            moduleType: fillingSlot.moduleType,
            positionName: fillingSlot.positionName,
            onFiled: (result) => {
              if (result.module_sn) modules.set(fillingSlot.positionCode, result.module_sn);
              setFiledOnSpot(result);
            },
          }}
          onClose={() => setFillingSlot(null)}
        />
      )}

      <ScrappedSerialPrompt
        serials={enteredSerials}
        status={serialStatus}
        onRemove={(serial) => {
          for (const [code, value] of Object.entries(scanner.values)) {
            if (normalizeSerial(value) === serial) scanner.setSlot(code, "");
          }
          setEntries((current) =>
            current.map((entry) =>
              normalizeSerial(entry.serial) === serial ? { ...entry, serial: "" } : entry,
            ),
          );
        }}
      />

      {confirmingDiscard && (
        <Modal
          title={t("archive.draftDeleteTitle")}
          onClose={() => setConfirmingDiscard(false)}
          footer={
            <>
              <button
                type="button"
                className="button-danger"
                onClick={async () => {
                  setConfirmingDiscard(false);
                  // Started before the form is cleared, which forgets where it was stored.
                  const dropped = userId ? dropDraft(userId) : Promise.resolve();
                  clearForm();
                  await dropped;
                  await refreshDrafts();
                }}
              >
                {t("archive.draftDiscard")}
              </button>
              <button type="button" data-autofocus onClick={() => setConfirmingDiscard(false)}>
                {t("common.cancel")}
              </button>
            </>
          }
        >
          <p>{t("archive.draftDiscardBody", { n: scanDone + entries.length })}</p>
        </Modal>
      )}

      {closing && (
        <Modal
          title={t("archive.closeTitle")}
          onClose={() => setClosing(null)}
          footer={
            <>
              <button
                type="button"
                className="button-primary"
                data-autofocus
                onClick={() => setClosing(null)}
              >
                {t("archive.closeKeepFilling")}
              </button>
              <button type="button" onClick={onClose}>
                {t("archive.closeConfirm")}
              </button>
            </>
          }
        >
          <p className={closing === "failed" ? "hint hint-error" : undefined}>
            {t(`archive.closeBody.${closing}`)}
          </p>
        </Modal>
      )}
    </Modal>
  );
}

type RowFilter = "all" | "critical" | "counted";

/** What a scan into the shared box came to: a part's slot, a module's, or a refusal. */
type FilingScanOutcome = ScanOutcome | ModuleScanOutcome | { kind: "robot-sn" };

const ROW_FILTERS: RowFilter[] = ["all", "critical", "counted"];

function BomSlots({
  bomPagination,
  rows,
  bomOptions,
  bomId,
  onBomChange,
  scanner,
  barcodeRules,
  installed,
  serialStatus,
  progress,
  nextModule,
  onScan,
  header,
  moduleSection,
  bomLocked = false,
  onSerialWidth,
}: {
  bomPagination: ReturnType<typeof useAvailableBoms>;
  bomOptions: {
    id: string;
    // Only published BOMs are offered, and a published BOM is always numbered.
    version: string | null;
    name: string | null;
  }[];
  bomId: string;
  onBomChange: (value: string) => void;
  rows: BomRow[];
  scanner: ReturnType<typeof useSlotScanner>;
  barcodeRules: BarcodeRules;
  installed: Map<string, InstalledSerial>;
  serialStatus: SerialStatus;
  /** Serials and modules together — what the count and submit go by. */
  progress: { done: number; total: number; complete: boolean };
  /** The module slot the next scan is for; modules come first (§9.2). */
  nextModule?: ModuleSlot;
  onScan: (raw: string) => FilingScanOutcome;
  /** Under the BOM: a module filing's type and SN. */
  header?: ReactNode;
  /** Above the parts: the module slots. */
  moduleSection?: ReactNode;
  /** Fixed by the filing a nested one was opened from. */
  bomLocked?: boolean;
  /** The width of a slot's serial box, in px, as it changes; null once there is none. */
  onSerialWidth?: (width: number | null) => void;
}) {
  const { t } = useTranslation();
  const [scanBuffer, setScanBuffer] = useState("");
  const serialHeader = useRef<HTMLTableCellElement | null>(null);
  const [lastOutcome, setLastOutcome] = useState<FilingScanOutcome | null>(null);
  // Opens on the critical parts — the ones with a serial, which is what this list is read
  // for — unless there are none (a machine filed without a BOM may have only counted
  // parts), where that would open on an empty list. `null` until the operator picks.
  const [chosen, setShown] = useState<RowFilter | null>(null);
  const shown: RowFilter = chosen ?? (rows.some((row) => row.isCritical) ? "critical" : "all");
  const [folded, setFolded] = useState<ReadonlySet<string>>(new Set());
  const toggleFold = (code: string) =>
    setFolded((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  const nodes = useMemo(() => foldNodes(rows), [rows]);
  const parents = parentKeys(nodes);
  // A filter shows every row it matches, wherever it sits in the tree.
  const narrowed = shown !== "all";
  const hidden = narrowed ? new Set<string>() : hiddenKeys(nodes, folded);
  const scanRef = useRef<HTMLInputElement | null>(null);
  // Which part the next scan is for, in the scan box itself — where the operator's eyes
  // are between scans. The BOM's remark ("左前电机") says it in shop-floor words; a slot
  // without one falls back to its position name, so the box never goes quiet mid-machine.
  const nextRow = rows.find((row) => row.positionCode === scanner.nextSlot);
  /** A refused scan in words: which part it was for, and the label formats it takes. */
  const formatMismatchText = (outcome: { positionCode: string }) => {
    const row = rows.find((candidate) => candidate.positionCode === outcome.positionCode);
    return {
      position: row?.remark ?? row?.positionName ?? outcome.positionCode,
      material: row?.materialCode ?? "",
      patterns: barcodeRules.patternsOf(row?.materialCode).join(" / "),
    };
  };
  const nextLabel = nextModule
    ? nextModule.positionName
    : nextRow
      ? (nextRow.remark ?? nextRow.positionName)
      : null;
  const scanPlaceholder = nextLabel
    ? t("archive.scanPlaceholder", { remark: nextLabel })
    : undefined;
  // The same sentence around a marker, so the remark can be set in bold between its two
  // halves in either language.
  const [scanPromptBefore = "", scanPromptAfter = ""] = t("archive.scanPlaceholder", {
    remark: "\u0000",
  }).split("\u0000");

  // Focus belongs to the scan box and stays there for the whole machine. A wedge scanner
  // types into whatever is focused, so handing focus to the slot input after each scan
  // makes the next scan append to the slot just filled — four modules end up in one field.
  // The highlighted row, not the caret, is what tells the operator where the next one goes.
  //
  // The box exists only once a BOM is chosen, so it takes focus when the slots appear —
  // not when this panel mounts, which is before then, while there is no box to focus.
  const hasSlots = progress.total > 0;
  useEffect(() => {
    if (hasSlots) scanRef.current?.focus();
  }, [hasSlots]);

  // The SN box above is drawn as wide as the serial boxes here, and follows the column
  // when it is dragged wider or narrower.
  useEffect(() => {
    const cell = serialHeader.current;
    if (!onSerialWidth) return;
    if (!cell || typeof ResizeObserver === "undefined") return;
    const report = () => onSerialWidth(Math.max(cell.offsetWidth - SLOT_CELL_PADDING, 0));
    report();
    const observer = new ResizeObserver(report);
    observer.observe(cell);
    return () => {
      observer.disconnect();
      onSerialWidth(null);
    };
  }, [hasSlots, onSerialWidth]);

  /**
   * Enter in a slot's own box — a scan into the slot that was clicked — moves on, as the
   * scan box does: to the next empty slot's box, or back to the scan box when that slot is
   * folded away (the scan box fills it all the same). Staying put would make the next scan
   * append to the serial just entered.
   */
  function moveOnFrom(positionCode: string) {
    const next = scanner.nextSlotAfter(positionCode);
    const box = next
      ? document.querySelector<HTMLInputElement>(`input[data-slot="${next}"]`)
      : null;
    (box ?? scanRef.current)?.focus();
  }

  return (
    <div className="panel">
      <div className="form-row">
        <label className="required" htmlFor="bom">
          BOM
        </label>
        <select
          id="bom"
          required
          value={bomId}
          disabled={bomLocked}
          onChange={(event) => onBomChange(event.target.value)}
        >
          <option value="">—</option>
          {bomOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {/* The name says what the version is for (量产版); the number alone does not.
                  Left out when there is none. */}
              {option.version}
              {option.name ? ` (${option.name})` : ""}
            </option>
          ))}
        </select>
      </div>

      <Pager
        hasNextPage={bomPagination.hasNextPage}
        isFetchingNextPage={bomPagination.isFetchingNextPage}
        onMore={() => void bomPagination.fetchNextPage()}
      />

      {header}

      {hasSlots && (
        <>
          <div className="scan-row">
            <label htmlFor="scan">{t("archive.scanHint")}</label>
            {/* The prompt is drawn over the box rather than left to the native placeholder,
                which is plain text: here the remark ("肩") is picked out, because it is what
                tells identical parts apart. The native placeholder stays, transparent, so
                assistive technology still reads the prompt. */}
            <div className="scan-input">
              {nextLabel && !scanBuffer && (
                <span className="scan-prompt" aria-hidden="true">
                  {scanPromptBefore}
                  <strong>{nextLabel}</strong>
                  {scanPromptAfter}
                </span>
              )}
              <input
                id="scan"
                ref={scanRef}
                value={scanBuffer}
                placeholder={scanPlaceholder}
                autoComplete="off"
                onChange={(event) => setScanBuffer(event.target.value)}
                // A keyboard-wedge scanner ends a scan with Enter — one an input method may
                // have taken (scanEnter.ts). The same box accepts typing, so manual entry
                // and scanning share one path (§10.3).
                {...scanEnterHandlers<HTMLInputElement>(
                  (target) => {
                    // The box's own value, not the state: a scan ended by a pause is read
                    // from a timer, after renders this closure has not seen.
                    if (!target.value.trim()) return;
                    setLastOutcome(onScan(target.value));
                    setScanBuffer("");
                  },
                  { burst: true },
                )}
              />
            </div>
          </div>

          {/* Always visible, per US-A-02: how many are left, not just whether it is done. */}
          <p className={`progress ${progress.complete ? "is-complete" : ""}`.trim()} role="status">
            <span>
              {progress.complete
                ? t("archive.complete")
                : t("archive.progress", {
                    done: progress.done,
                    total: progress.total,
                    remaining: progress.total - progress.done,
                  })}
            </span>
            {/* §10.3 wants the unfilled slots highlighted and the first one reachable. On a
                forty-slot BOM the gap can be off screen, and "还差 1 个" without a way to
                find it is a puzzle rather than an instruction. */}
            {!scanner.isComplete && scanner.nextSlot && (
              <button
                type="button"
                className="progress-locate"
                onClick={() => {
                  // The slot being located may be filtered out of the table right now, and
                  // scrolling to an element that is not rendered does nothing at all —
                  // which reads as a dead button. Show the criticals first, then scroll.
                  if (shown === "counted") setShown("critical");
                  const slot = scanner.nextSlot ?? "";
                  // Folded away counts as "not rendered" too: open the tree to reach it.
                  if (hidden.has(slot)) setFolded(new Set());
                  requestAnimationFrame(() => {
                    document
                      .querySelector<HTMLInputElement>(`[aria-label="${CSS.escape(slot)}"]`)
                      ?.scrollIntoView({ block: "center", behavior: "smooth" });
                  });
                }}
              >
                {t("archive.locateFirstEmpty")}
              </button>
            )}
          </p>
        </>
      )}

      {/* Here, right under the scan box and the count, because that is where the operator
          is looking when the scan that caused it lands. */}
      <InstalledWarning installed={installed} />

      {lastOutcome &&
        (lastOutcome.kind === "module-no-slot" || lastOutcome.kind === "robot-sn") && (
          <div className="banner banner-error" role="alert">
            {t(`moduleFiling.scan.${lastOutcome.kind}`, {
              module: "moduleType" in lastOutcome ? lastOutcome.moduleType : "",
            })}
          </div>
        )}

      {moduleSection}

      {rows.length > 0 && (
        <>
          {/* Counts come from the whole list, not the filtered one: a filter answers
              "show me less", never "there is less". */}
          <div className="filters filters-inline" role="group" aria-label={t("archive.showRows")}>
            {ROW_FILTERS.map((option) => (
              <button
                key={option}
                type="button"
                className={shown === option ? "chip chip-on" : "chip"}
                aria-pressed={shown === option}
                onClick={() => setShown(option)}
              >
                {t(`archive.filter.${option}`, { n: countOf(rows, option) })}
              </button>
            ))}
            <FoldControls
              levels={levelCount(nodes)}
              disabled={narrowed}
              onLevel={(level) =>
                setFolded(level === null ? new Set() : foldedToLevel(nodes, level))
              }
            />
          </div>

          {/* -3: the serial column is wide by default now, and widths remembered from the
              narrow default would keep it as it was. */}
          <ResizableTable storageKey="filing-slots-3" className="slots">
            <thead>
              <tr>
                {/* As on the BOM and the record: where on the machine, first. */}
                <th className="col-part-name">{t("bom.partName")}</th>
                <th>{t("archive.position")}</th>
                <th>{t("bom.drawingNo")}</th>
                <th>{t("archive.materialCode")}</th>
                <th>{t("bom.remark")}</th>
                <th className="col-serial" ref={serialHeader}>
                  {t("archive.serial")}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows
                .filter((row) => matches(row, shown) && !hidden.has(row.positionCode))
                .map((row) => {
                  const isNext = scanner.nextSlot === row.positionCode;
                  const isFilled = Boolean(scanner.values[row.positionCode]);
                  const elsewhere = installed.get(
                    normalizeSerial(scanner.values[row.positionCode] ?? ""),
                  );
                  const misfit = scanner.formatMismatches.includes(row.positionCode);
                  return (
                    <tr
                      key={row.positionCode}
                      className={
                        !row.isCritical
                          ? "slot-counted"
                          : elsewhere || misfit
                            ? "slot-installed"
                            : isNext
                              ? "slot-next"
                              : isFilled
                                ? "slot-filled"
                                : undefined
                      }
                    >
                      <td>{row.partName || <span className="muted">—</span>}</td>
                      <td>
                        {parents.has(row.positionCode) && !narrowed ? (
                          <button
                            type="button"
                            className={
                              folded.has(row.positionCode)
                                ? "row-toggle tooltip-trigger"
                                : "row-toggle row-toggle-open tooltip-trigger"
                            }
                            aria-expanded={!folded.has(row.positionCode)}
                            aria-label={t(
                              folded.has(row.positionCode) ? "tree.expand" : "tree.collapse",
                              { name: row.positionName },
                            )}
                            data-tooltip={t(
                              folded.has(row.positionCode) ? "tree.expand" : "tree.collapse",
                              { name: row.positionName },
                            )}
                            onClick={() => toggleFold(row.positionCode)}
                          >
                            <ChevronIcon size={14} />
                          </button>
                        ) : (
                          <span className="row-toggle-spacer" />
                        )}
                        {row.positionName}
                      </td>
                      <td className="mono">{row.drawingNo || <span className="muted">—</span>}</td>
                      <td className="mono">
                        {row.materialCode ?? <span className="muted">—</span>}
                      </td>
                      {/* The line's note, on every one of its slots, right after the
                          material number it most often qualifies ("核对固件版本"): the
                          person holding the scanner reads the row they are filling. */}
                      <td className="line-remark">{row.remark}</td>
                      <td>
                        {row.isCritical ? (
                          <>
                            <input
                              aria-label={row.positionCode}
                              data-slot={row.positionCode}
                              value={scanner.values[row.positionCode] ?? ""}
                              autoComplete="off"
                              onChange={(event) =>
                                scanner.setSlot(row.positionCode, event.target.value)
                              }
                              {...scanEnterHandlers<HTMLInputElement>(
                                () => moveOnFrom(row.positionCode),
                                { burst: true },
                              )}
                            />
                            {misfit ? (
                              <BarcodeMismatchNote
                                materialCode={row.materialCode}
                                patterns={barcodeRules.patternsOf(row.materialCode)}
                              />
                            ) : (
                              <SerialStatusNote
                                state={serialStatus.of(scanner.values[row.positionCode] ?? "")}
                              />
                            )}
                          </>
                        ) : (
                          // No input: the server files this line from the BOM at this
                          // quantity and rejects a serial against it (§6.2).
                          <span className="muted">{t("archive.counted", { n: row.quantity })}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </ResizableTable>

          {lastOutcome &&
            lastOutcome.kind !== "filled" &&
            lastOutcome.kind !== "module-filled" &&
            lastOutcome.kind !== "module-no-slot" &&
            lastOutcome.kind !== "robot-sn" && (
              <div className="banner banner-error" role="alert">
                {t(`archive.scan.${lastOutcome.kind}`, {
                  serial: "serial" in lastOutcome ? lastOutcome.serial : "",
                  ...(lastOutcome.kind === "format-mismatch" && formatMismatchText(lastOutcome)),
                })}
              </div>
            )}

          {scanner.duplicates.length > 0 && (
            <div className="banner banner-error">
              {t("archive.duplicateSerials", { serials: scanner.duplicates.join(", ") })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FreeEntries({
  entries,
  onChange,
  installed,
  serialStatus,
}: {
  entries: FreeEntry[];
  onChange: (entries: FreeEntry[]) => void;
  installed: Map<string, InstalledSerial>;
  serialStatus: SerialStatus;
}) {
  const { t } = useTranslation();

  const update = (index: number, patch: Partial<FreeEntry>) =>
    onChange(entries.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));

  return (
    <section className="panel">
      <h2 className="panel-title">{t("archive.freeEntries")}</h2>
      {/* No header-only table before the first entry: an empty grid with the 「新增条目」
          button stranded under it reads as a broken layout rather than an empty list. */}
      {entries.length === 0 ? (
        <p className="hint">{t("archive.noEntriesYet")}</p>
      ) : (
        <table className="entries">
          <thead>
            <tr>
              <th>{t("archive.position")}</th>
              <th>{t("archive.materialCode")}</th>
              <th>{t("archive.serial")}</th>
              <th>{t("archive.quantity")}</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, index) => (
              <tr key={index}>
                <td>
                  <input
                    aria-label={`position-name-${index}`}
                    value={entry.position_name}
                    onChange={(event) => update(index, { position_name: event.target.value })}
                  />
                </td>
                <td>
                  <input
                    aria-label={`material-code-${index}`}
                    className="mono"
                    value={entry.material_code}
                    onChange={(event) => update(index, { material_code: event.target.value })}
                  />
                </td>
                <td>
                  <input
                    aria-label={`serial-${index}`}
                    value={entry.serial}
                    onChange={(event) =>
                      // A serial identifies one physical unit (§6.3), so entering one pins
                      // the quantity to 1 rather than letting the server reject it later.
                      update(index, {
                        serial: event.target.value,
                        quantity: event.target.value ? 1 : entry.quantity,
                      })
                    }
                  />
                  <SerialStatusNote state={serialStatus.of(entry.serial)} />
                </td>
                <td>
                  <input
                    aria-label={`quantity-${index}`}
                    type="number"
                    min={1}
                    disabled={Boolean(entry.serial)}
                    value={entry.quantity}
                    onChange={(event) => update(index, { quantity: Number(event.target.value) })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <InstalledWarning installed={installed} />
      <button type="button" onClick={() => onChange([...entries, { ...BLANK_ENTRY }])}>
        <AddLabel>{t("archive.addEntry")}</AddLabel>
      </button>
    </section>
  );
}

/**
 * Every entered serial that is on another machine, each with where it is: the archive
 * (linked, in a new tab so the half-filed machine stays put), the device number, the
 * product type, the position and since when. What the operator needs to decide whether
 * the part in hand is mislabelled, or the other record is wrong.
 */
function InstalledWarning({ installed }: { installed: Map<string, InstalledSerial> }) {
  const { t } = useTranslation();
  if (installed.size === 0) return null;
  return (
    <div className="banner banner-error" role="alert">
      <p>{t("archive.installedTitle", { n: installed.size })}</p>
      <ul className="installed-list">
        {[...installed.values()].map((hit) => (
          <li key={hit.serial_normalized}>
            {t("archive.installedAt", {
              serial: hit.serial_raw,
              deviceNo: hit.device_no ?? hit.archive_no,
              productType: hit.product_type_name,
              position: hit.position_name,
              time: new Date(hit.installed_at).toLocaleString(),
            })}{" "}
            <Link to={`/archives/${hit.archive_id}`} target="_blank" rel="noreferrer">
              {hit.archive_no}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The rows as a tree: a row sits under its line's parent line — under that line's row,
 * or, for a parent expanded into slots, its last slot, the row directly above it.
 */
function foldNodes(rows: BomRow[]): FoldNode[] {
  const rowOfLine = new Map<string, string>();
  for (const row of rows) {
    if (row.lineId) rowOfLine.set(row.lineId, row.positionCode);
  }
  return rows.map((row) => ({
    key: row.positionCode,
    parentKey: row.parentLineId ? (rowOfLine.get(row.parentLineId) ?? null) : null,
    depth: row.depth,
  }));
}

function matches(row: BomRow, shown: RowFilter): boolean {
  return shown === "all" || (shown === "critical") === row.isCritical;
}

/** `.slots td`'s left and right padding: a serial box is its cell less this. */
const SLOT_CELL_PADDING = 28;

function countOf(rows: BomRow[], shown: RowFilter): number {
  return rows.filter((row) => matches(row, shown)).length;
}

/** The SN already printed on the machine's or module's label, when there is one. Left
 *  blank — the usual case — the server generates it at filing. */
function SnField({
  value,
  onChange,
  invalid,
  width,
  label,
}: {
  /** Whose SN: 「整机 SN」 or 「模组 SN」 — not 「序列号」, which on this screen means a part's. */
  label: string;
  value: string;
  onChange: (value: string) => void;
  invalid: string | null;
  /** The slots' serial box width, to match; null before there are slots. */
  width: number | null;
}) {
  const { t } = useTranslation();
  return (
    <div className="form-row">
      <div className="form-row-label-hint">
        <label htmlFor="filing-sn">{label}</label>
        <span
          className="field-hint-trigger tooltip-trigger"
          role="img"
          tabIndex={0}
          data-tooltip={t("archive.snOptionalHint")}
          aria-label={t("archive.snOptionalHint")}
        >
          <InfoIcon size={15} />
        </span>
      </div>
      <div className="filing-sn" style={width ? { width } : undefined}>
        <input
          id="filing-sn"
          autoComplete="off"
          placeholder={t("archive.snOptionalPlaceholder")}
          aria-invalid={invalid ? true : undefined}
          aria-describedby={invalid ? "filing-sn-error" : undefined}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        {invalid && (
          <p className="hint hint-error" id="filing-sn-error">
            {invalid}
          </p>
        )}
      </div>
    </div>
  );
}
